import { ApolloError } from '@apollo/client';
import { isNil } from 'lodash';
import { Fragments, Mutations, Queries } from '@shared/api';
import { getSharedApolloClient } from '@shared/api/client';
import * as NotifyPendingUsersActions from '@shared/scenes/chat/notifyPendingUsers/store/actions';
import * as UploadActions from '@shared/scenes/upload/store/actions';
import { getStore } from '@shared/store';
import { Navigator } from '@shared/util/navigation';
import { notNil } from '@shared/util/notNil';
import { showRateAppPopupSeries } from '@shared/util/rateApp';
import { MBChannelTypeUtil } from './channelType';
import * as MBMessagePersist from './persist';

export class MBMessageQueue {
  /*
        Dict containing promises of channels sending messages
    */
  channelLock: Dict<Promise<void>> = {};
  navigator: Navigator | undefined = undefined;

  get apolloClient() {
    return getSharedApolloClient();
  }

  get store() {
    return getStore();
  }

  /*
        Add a new message to the queue of messages to send for a certain channel.
    */
  async add(message: MBAPI.ChatMessage, channelSlug: string): Promise<void> {
    this.store.dispatch({ type: 'CHAT:ADD_MESSAGE_TO_CACHE', value: { channelSlug, message } });
    await this.updateStatus(message, 'waiting');
    // Send all messages in the channel in the correct order
    await this.lockAndSendMessagesInChannel(channelSlug);
  }

  /*
        Delete/cancel a message from a channel based on its current delivery state:
          - failed/uploadingAttachments: never reached the server, just remove from cache
          - waiting: queued but not yet sent, just remove from cache
          - sending: API request may be in flight, remove from cache and best-effort server delete
          - sent/undefined: on the server, remove from cache and delete via API
    */
  async delete(message: MBAPI.ChatMessage, channelSlug: string) {
    const store = getStore();
    const statusFromUuid = store.getState().chatMessages.deliveryStatusFromMessageUuid;
    const deliveryStatus = statusFromUuid[message.meta.uuid ?? ''];

    await this.removeMessageFromCache(message, channelSlug);

    if (
      deliveryStatus === 'failed' ||
      deliveryStatus === 'waiting' ||
      deliveryStatus === 'uploadingAttachments'
    ) {
      return;
    }

    if (deliveryStatus === 'sending') {
      try {
        await this.performDeleteAPIRequest(message, channelSlug);
      } catch {
        // Message may not have been persisted server-side yet
      }
      return;
    }

    await this.performDeleteAPIRequest(message, channelSlug);
  }

  /*
        Retry sending a messages in a particular channel
    */
  async retry(channelSlug: string): Promise<void> {
    return this.lockAndSendMessagesInChannel(channelSlug);
  }

  /*
        Remove a message from the queue for a certain channel.
    */
  async removeMessageFromCache(message: MBAPI.ChatMessage, channelSlug: string) {
    this.store.dispatch({
      type: 'CHAT:REMOVE_MESSAGE_FROM_CACHE',
      value: { channelSlug, message },
    });
    await this.persist();
  }

  /*
        Send all messages in a channel, ordered by when they
        were added to the queue. This basically just makes
        sure that the channel is properly locked. All the
        sending logic happens in this.sendMessagesInChannel
    */
  lockAndSendMessagesInChannel(channelSlug: string): Promise<void> {
    // If we're already sending messages for this channel, return
    // the promise for that group of requests.
    if (this.isLocked(channelSlug)) {
      return this.channelLock[channelSlug];
    }

    // Wrap the logic in an async function so that this function
    // doesn't have to be async. In this way, we can return the
    // current sending promise if locked, which is a lot easier
    // to test.
    const promise = (async () => {
      try {
        await this.sendMessagesInChannel(channelSlug);
      } finally {
        // We will unlock the channel no matter if the promise succeeds or not
        this.unlock(channelSlug);
      }
    })();

    this.lock(channelSlug, promise);
    return promise;
  }

  /*
        Send all sendable messages in a channel one by one, skipping
        messages that have already failed or been sent. This allows new
        messages to be delivered even when earlier messages are stuck.
    */
  async sendMessagesInChannel(channelSlug: string): Promise<void> {
    while (true) {
      const state = this.store.getState();
      const uuids = state.chatMessages.cachedMessageUuidsFromChannelSlug[channelSlug] ?? [];

      if (!uuids.length) {
        break;
      }

      const nextUuid = uuids.find(uuid => {
        const status = state.chatMessages.deliveryStatusFromMessageUuid[uuid];
        return status !== 'failed' && status !== 'sent';
      });

      if (!nextUuid) {
        break;
      }

      const message = state.chatMessages.cachedMessages[nextUuid];

      if (!message) {
        break;
      }

      if (!message.content && !message.meta.attachments.length) {
        await this.removeMessageFromCache(message, channelSlug);
        continue;
      }

      await this.send(message, channelSlug);
    }
  }

  /*
        Check if there are any messages left in a channel.
    */
  hasMessagesToSend(channelSlug: string): boolean {
    const uuidsFromSlug = this.store.getState().chatMessages.cachedMessageUuidsFromChannelSlug;
    return !!uuidsFromSlug[channelSlug] && !!uuidsFromSlug[channelSlug].length;
  }

  /*
        Send a message to the API. In words, this is what happens:
          1. Upload all attachments (all local files, not eg Giphy)
          2. Do the API mutation
          3. Insert the new message to the Apollo cache
          4. Remove the message from cache (so we don't resend it etc)
    */
  async send(message: MBAPI.ChatMessage, channelSlug: string): Promise<void> {
    try {
      await this.updateStatus(message, 'uploadingAttachments');
      await this.uploadAttachments(message);
      await this.updateStatus(message, 'sending');

      // for testing failed send
      // throw new Error('Simulated send failure');

      if (MBChannelTypeUtil.isThread(channelSlug)) {
        const threadId = MBChannelTypeUtil.parseThreadIdFromChannelSlug(channelSlug);
        if (!isNil(threadId)) {
          const createdMessage = await this.performSendThreadAPIRequest(message, threadId);
          if (!isNil(createdMessage)) {
            this.handleThreadMessageSent(createdMessage, threadId);
          }
        }
      } else {
        const response = await this.performSendChannelAPIRequest(message, channelSlug);
        this.handleChannelMessageSent(response, channelSlug);
      }

      await this.removeMessageFromCache(message, channelSlug);
    } catch (error) {
      await this.handleMessageFailed(message, channelSlug, error as Error);
      await this.persist();
      // Don't rethrow — let the queue continue to the next message
      // instead of blocking all subsequent sends in this channel.
    }
  }

  /*
        Uploads all attachments for a message. Only local files
        (aka files with non-public urls) are uploaded. After they've
        been uploaded, their url is updated to the public url. So
        this function actually mutates the message object. Not ideal
        but it's the smoothest way I can think of to handle this nicely.
    */
  async uploadAttachments(message: MBAPI.ChatMessage): Promise<void> {
    const uploadPs = message.meta.attachments
      .filter(notNil)
      // If the attachment is already a public url (has been
      // uploaded or was eg GIF) we don't want to upload it.
      .filter(attachment => attachment.url.indexOf('http') !== 0)
      .map(async attachment => {
        const publicUrl = await UploadActions.uploadFile(this.apolloClient, {
          id: attachment.fileKey,
          url: attachment.url,
          name: attachment.name,
          contentType: attachment.contentType ?? '',
        });
        // After we've uploaded the file, we change the attachment's url. This is
        // generally bad practice since we mutate a parameter to the function.
        // However, in the context where we will later use the message (and its
        // attachments), this is the least dirty.
        // TODO(fant): how do we make the attachment list transition smoothly here?

        attachment.url = publicUrl;
      });
    await Promise.all(uploadPs);
  }

  /*
        Map a "real" chat attachment to the GraphQL input type when creating a message.
    */
  private toAttachment = (attachment: MBAPI.ChatAttachment): MBAPI.NewAttachment => ({
    fileKey: attachment.fileKey,
    originalFilename: attachment.name,
    publicUrl: attachment.url,
    width: attachment.width,
    height: attachment.height,
  });

  /*
        Create the actual message through the API.
    */
  async performSendChannelAPIRequest(
    message: MBAPI.ChatMessage,
    channelSlug: string,
  ): Promise<MBAPI.SendMessageMutation['createMessage']> {
    const response = await this.apolloClient.mutate<
      MBAPI.SendMessageMutation,
      MBAPI.SendMessageMutationVariables
    >({
      mutation: Mutations.chat.sendMessage,
      variables: {
        channelSlug,
        content: message.content,
        urgent: message.urgent,
        attachments: message.meta.attachments.filter(notNil).map(this.toAttachment),
        uuid: message.meta.uuid ?? '',
      },
    });

    // return actual response (message and pendingUsers)
    return response.data?.createMessage ?? null;
  }

  async performSendThreadAPIRequest(
    message: MBAPI.ChatMessage,
    threadId: string,
  ): Promise<MBAPI.ChatMessage | undefined> {
    const response = await this.apolloClient.mutate<
      MBAPI.SendThreadMessageMutation,
      MBAPI.SendThreadMessageMutationVariables
    >({
      mutation: Mutations.thread.sendMessage,
      variables: {
        input: {
          threadId,
          content: message.content,
          type: 'TEXT',
          isImportant: !!message.urgent,
          meta: {
            attachments: message.meta.attachments.filter(notNil).map(this.toAttachment),
            uuid: message.meta.uuid ?? '',
          },
        },
      },
    });

    return response.data?.createdMessage as MBAPI.ChatMessage | undefined;
  }

  /*
        When a message has been successfully sent, we want to:
          1. Remove it from cache (so it doesn't appear as "sending" anymore)
          2. Update the channel's messages to also contain the new message
        Note: this was run in apolloClient.mutate's update function. This gives
        the same result as running it just after mutate is done though.
    */
  handleChannelMessageSent(
    createdMessage: MBAPI.SendMessageMutation['createMessage'],
    channelSlug: string,
  ) {
    if (!createdMessage) {
      return;
    }
    const { message, recentMessageCount } = createdMessage;
    const pendingUsers = createdMessage.pendingUsers?.filter(notNil);

    // Get the chat thread data and insert the message as the first
    // message in the thread. Note: we use the same object (no {...}),
    // so React's shallow diff will think nothing has changed, which
    // prevents all chat messages from re-rendering.
    const data = this.apolloClient.readQuery<MBAPI.ChannelChatThreadQuery>({
      query: Queries.channel.chatThread,
      variables: { slug: channelSlug } as MBAPI.ChannelChatThreadQueryVariables,
    });

    // Note: sometimes the subscription is faster than the API request
    // Therefore we need to make sure the message doesn't already exist
    // among the channel's messages.
    const hasData = !isNil(data);
    const hasMessage = data?.channel?.messages?.filter(notNil).some(m => m.id === message.id);
    if (hasData && !hasMessage) {
      this.apolloClient.writeQuery({
        data: {
          ...data,
          channel: {
            ...data.channel,
            messages: [message, ...(data.channel?.messages ?? [])],
          },
        },
        query: Queries.channel.chatThread,
        variables: { slug: channelSlug } as MBAPI.ChannelChatThreadQueryVariables,
      });
    }

    NotifyPendingUsersActions.updatePendingUsers({ channelSlug, message, pendingUsers });

    showRateAppPopupSeries(recentMessageCount, this.navigator);
  }

  handleThreadMessageSent(message: MBAPI.ChatMessage, threadId: string) {
    const data = this.apolloClient.readQuery<
      MBAPI.CardChatThreadQuery,
      MBAPI.CardChatThreadQueryVariables
    >({
      query: Queries.card.chatThread,
      variables: { id: threadId },
    });

    /**
     * This flow is skipped in the two scenarios:
     *  - Thread is not loaded in Apollo Cache (user reconnected in a different screen)
     *  - Message is already included in the cache's Thread
     */
    if (!data?.thread || !!data?.thread?.messages?.some(m => m.id === message.id)) {
      return;
    }

    const thread = data.thread;
    const messageCount = thread.messageCount ?? 0;
    const membership = thread.memberships?.find(m => m.user.isMe);
    const now = new Date().toISOString();

    let updatedMemberships = thread.memberships ?? [];
    if (!!membership) {
      updatedMemberships = updatedMemberships.map(m =>
        m.user.isMe
          ? {
              ...m,
              readMessageCount: m.readMessageCount + 1,
              messagesViewedAt: now,
            }
          : m,
      );
    } else {
      const user = this.apolloClient.readFragment<MBAPI.ShortUser>({
        id: `User${this.store.getState().appState.currentUserId}`,
        fragment: Fragments.user.short,
      });

      if (!isNil(user)) {
        updatedMemberships = [
          ...updatedMemberships,
          {
            readMessageCount: 1,
            messagesViewedAt: now,
            user,
          },
        ];
      }
    }

    this.apolloClient.writeQuery({
      data: {
        thread: {
          ...thread,
          messageCount: messageCount + 1,
          memberships: updatedMemberships,
          messages: [
            {
              ...message,
              type: message.type as unknown,
              subType: message.subType as unknown,
            } as any,
            ...(thread.messages ?? []),
          ],
        },
      },
      query: Queries.card.chatThread,
      variables: { id: threadId },
    });
  }

  /*
        When a message fails to send, we want to update the delivery status
        depending on the error. Most of the time we mark it as failed. However
        in some scenarios (eg when there's no internet connection), we mark it
        as "waiting".
    */
  async handleMessageFailed(message: MBAPI.ChatMessage, channelSlug: string, error: Error) {
    // TODO(fant): move to some smarter place (or find the real TS type for this)
    interface NetworkError {
      response: {
        type: 'default';
        status: number;
        ok: boolean;
        headers: any;
        url: string;
      };
    }

    // If something goes wrong (eg because of network or bad server response),
    // update the status to failed. All failed messages will be submitted later
    // (eg when internet is back).
    if (error instanceof ApolloError) {
      const apolloError = error as ApolloError;
      const networkError: NetworkError = apolloError.networkError as any;

      // Note(fant): we need to do something with the error here
      // otherwise Apollo will throw a red screen
      // TODO(fant): figure out the type of the network errors
      if (!!apolloError.graphQLErrors && !!apolloError.graphQLErrors.length) {
        // When the API fails to send a message, we usually want to mark it
        // as "Failed to send". However, not all the time. There are certain
        // errors where we simply want to remove the messages from the client.
        // For instance, messages connected to issue #2288 where an invalid
        // channel slug is used (they will fail forever). In that case, we
        // want to call removeMessageFromCache and not try to resend the
        // message (just like when a message) has succeeded.
        if (apolloError.graphQLErrors.some(err => err.message.includes('Channel not found'))) {
          await this.removeMessageFromCache(message, channelSlug);
        }

        this.updateStatus(message, 'failed');
      } else if (!!networkError && !!networkError.response) {
        // const httpStatus = networkError.response.status;
        this.updateStatus(message, 'failed');
      } else {
        // Probably due to no internet connection
        // TODO(fant): do we want to update internet connection status when we get this?
        this.updateStatus(message, 'waiting');
      }
    } else {
      // If something else goes wrong (eg file uploading)
      this.updateStatus(message, 'failed');
    }
  }

  /*
        Delete a message through the API. Instead of using Apollo's built in
        optimisticResponse, we handle the optimstic part manually. The reason
        we do this is because Apollo (persumably) copies all messages into
        a new "optimistic" cache, which causes the whole chat list to rerender
        because the objects have changed (aka shallow diffing won't work).
        This is shit for performance.
    */
  async performDeleteAPIRequest(message: MBAPI.ChatMessage, channelSlug: string) {
    if (MBChannelTypeUtil.isThread(channelSlug)) {
      try {
        const threadId = MBChannelTypeUtil.parseThreadIdFromChannelSlug(channelSlug);
        const data = this.apolloClient.readFragment<MBAPI.Thread>({
          id: `Thread${threadId}`,
          fragment: Fragments.thread.thread,
          fragmentName: 'Thread',
        });

        this.apolloClient.writeFragment({
          data: {
            ...data,
            messages: data?.messages?.filter(m => m.id !== message.id),
          },
          id: `Thread${threadId}`,
          fragment: Fragments.thread.thread,
          fragmentName: 'Thread',
        });
      } catch (err) {
        // don't care
      }

      await this.apolloClient.mutate<
        MBAPI.DeleteThreadMessageMutation,
        MBAPI.DeleteThreadMessageMutationVariables
      >({
        mutation: Mutations.thread.deleteMessage,
        variables: { id: message.id },
      });
    } else {
      try {
        this.optimisticallyUpdateMessage(
          message,
          { type: 'SYSTEM', subType: 'MESSAGE_DELETED' },
          channelSlug,
        );

        await this.apolloClient.mutate<
          MBAPI.DeleteMessageMutation,
          MBAPI.DeleteMessageMutationVariables
        >({
          mutation: Mutations.chat.deleteMessage,
          variables: { messageId: message.id },
        });
      } catch (error) {
        // revert optimistic update
        this.optimisticallyUpdateMessage(message, { type: 'TEXT', subType: null }, channelSlug);
        throw error;
      }
    }
  }

  async performEditAPIRequest(message: MBAPI.ChatMessage, content: string, channelSlug: string) {
    try {
      this.optimisticallyUpdateMessage(message, { content }, channelSlug);

      // TODO(fant): solve this in a more general way through the API.
      if (MBChannelTypeUtil.isThread(channelSlug)) {
        await this.apolloClient.mutate<
          MBAPI.EditThreadMessageMutation,
          MBAPI.EditThreadMessageMutationVariables
        >({
          mutation: Mutations.thread.editMessage,
          variables: {
            input: {
              content,
              id: message.id,
            },
          },
        });
      } else {
        await this.apolloClient.mutate<
          MBAPI.EditMessageMutation,
          MBAPI.EditMessageMutationVariables
        >({
          mutation: Mutations.chat.editMessage,
          variables: {
            content,
            messageId: message.id,
          },
        });
      }
    } catch (error) {
      // revert optimistic update
      this.optimisticallyUpdateMessage(message, { content: message.content }, channelSlug);
      throw error;
    }
  }

  /*
        Update a message optimistically in the Apollo store.
    */
  optimisticallyUpdateMessage(
    message: MBAPI.ChatMessage,
    optimistic: Partial<MBAPI.ChatMessage>,
    channelSlug: string,
  ) {
    if (MBChannelTypeUtil.isThread(channelSlug)) {
      const data = this.apolloClient.readFragment<MBAPI.ThreadMessage>({
        id: `ThreadMessage${message.id}`,
        fragment: Fragments.thread.message,
        fragmentName: 'ThreadMessage',
      });

      Object.assign({}, data, optimistic);
      this.apolloClient.writeFragment({
        data,
        id: `ThreadMessage${message.id}`,
        fragment: Fragments.thread.message,
        fragmentName: 'ThreadMessage',
      });
    } else {
      const data = this.apolloClient.readFragment<MBAPI.ChatMessage>({
        id: `Message${message.id}`,
        fragment: Fragments.chat.message,
        fragmentName: 'ChatMessage',
      });
      this.apolloClient.writeFragment({
        data: Object.assign({}, data, optimistic),
        id: `Message${message.id}`,
        fragment: Fragments.chat.message,
        fragmentName: 'ChatMessage',
      });
    }
  }

  async updateStatus(message: MBAPI.ChatMessage, status: MBDeliveryStatus) {
    this.store.dispatch({
      type: 'CHAT:UPDATE_MESSAGE_DELIVERY_STATUS',
      value: { message, status },
    });
    await this.persist();
  }

  /*
        Mark the channel as locked by storing the promise for
        sending all messages in it. This prevents us from starting
        to send the same messages in the same channel multiple times.
    */
  lock(channelSlug: string, promise: Promise<void>) {
    this.channelLock[channelSlug] = promise;
  }

  /*
        Unlock a channel to make it possible to send all messages in it anew.
    */
  unlock(channelSlug: string) {
    delete this.channelLock[channelSlug];
  }

  /*
        Check if a channel is currently locked.
    */
  isLocked(channelSlug): boolean {
    return !!this.channelLock[channelSlug];
  }

  /*
        Persist all messages in the store
    */
  async persist(): Promise<void> {
    await MBMessagePersist.persist(this.store.getState());
  }

  setNavigator(navigator: Navigator) {
    this.navigator = navigator;
  }
}

export const messageQueue = new MBMessageQueue();
